/** OnlyFans user-list payloads → what the Collections page shows (shapes verified live 2026-10-07). */

export interface Avatar {
  id: number
  name: string
  url: string
}

export interface FanList {
  /** Numeric for custom lists; "fans", "following", "friends", … for OnlyFans' own. */
  id: string
  type: string
  name: string
  users: number
  posts: number
  canUpdate: boolean
  canDelete: boolean
  /** OnlyFans accepts fans into it. */
  canAddUsers: boolean
  /** Its members can be removed (and the list cleared). */
  canManageUsers: boolean
  /** Up to three members, when the lists were asked with skip_users=dups. */
  previews: Avatar[]
}

export type FanStatus = 'active' | 'expired' | null

export interface FanListState {
  id: string
  name: string
  hasUser: boolean
  canAddUser: boolean
}

export interface Fan {
  id: number
  name: string
  username: string
  /** The creator's own name for this fan, if set. */
  displayName: string
  avatar: string
  header: string
  verified: boolean
  lastSeen: string | null
  status: FanStatus
  restricted: boolean
  blocked: boolean
  /** USD the fan has spent on this account, when OnlyFans says. */
  spent: number | null
  subscribedAt: string | null
  /** The lists OnlyFans reports for this fan (listsStates). */
  lists: FanListState[]
}

export type FanFilter = 'all' | 'active' | 'expired' | 'restricted' | 'blocked'

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

function thumb(raw: Raw, field: string, thumbs: string, sizes: string[]): string {
  const set = (raw[thumbs] ?? {}) as Record<string, unknown>
  for (const s of sizes) if (str(set[s])) return str(set[s])
  return str(raw[field])
}

export function avatarOf(raw: Raw): string {
  return thumb(raw, 'avatar', 'avatarThumbs', ['c144', 'c50'])
}

export function toList(raw: Raw): FanList {
  const users = Array.isArray(raw.users) ? (raw.users as Raw[]) : []
  return {
    id: String(raw.id),
    type: str(raw.type),
    name: str(raw.name),
    users: num(raw.usersCount),
    posts: num(raw.postsCount),
    canUpdate: raw.canUpdate === true,
    canDelete: raw.canDelete === true,
    canAddUsers: raw.canAddUsers === true,
    canManageUsers: raw.canManageUsers === true,
    previews: users
      .filter((u) => u.id != null)
      .slice(0, 3)
      .map((u) => ({ id: num(u.id), name: str(u.displayName) || str(u.name), url: thumb(u, 'avatar', 'avatarThumbs', ['c50', 'c144']) })),
  }
}

function statusOf(raw: Raw): FanStatus {
  if (raw.subscribedOnExpiredNow === true) return 'expired'
  if (raw.subscribedOn === true) return 'active'
  return null
}

export function toFan(raw: Raw): Fan {
  const sub = (raw.subscribedOnData ?? {}) as Raw
  const states = Array.isArray(raw.listsStates) ? (raw.listsStates as Raw[]) : []
  return {
    id: num(raw.id),
    name: str(raw.name),
    username: str(raw.username),
    displayName: str(raw.displayName),
    avatar: avatarOf(raw),
    header: thumb(raw, 'header', 'headerThumbs', ['w480', 'w760']),
    verified: raw.isVerified === true,
    lastSeen: str(raw.lastSeen) || null,
    status: statusOf(raw),
    restricted: raw.isRestricted === true,
    blocked: raw.isBlocked === true,
    spent: typeof sub.totalSumm === 'number' ? sub.totalSumm : null,
    subscribedAt: str(sub.subscribeAt) || null,
    lists: states.map((s) => ({ id: String(s.id), name: str(s.name), hasUser: s.hasUser === true, canAddUser: s.canAddUser === true })),
  }
}

/** The name the creator sees for a fan. */
export function fanLabel(f: Fan): string {
  return f.displayName || f.name || f.username
}

/**
 * Status chips a list can filter by on OnlyFans' side. Fans and Following are
 * OnlyFans' subscription lists, which take a type; custom lists ignore one
 * (verified live), so they get no chips rather than chips that lie.
 */
export function filtersFor(list: FanList | null): FanFilter[] {
  if (list?.id === 'fans') return ['all', 'active', 'expired', 'restricted', 'blocked']
  if (list?.id === 'following') return ['all', 'active', 'expired']
  return []
}

/** The list and filter a `view=` asks for, or null. */
export function viewTarget(query: string | undefined): { list: string; filter: FanFilter } | null {
  const view = new URLSearchParams(query ?? '').get('view')
  if (view === 'fans' || view === 'following') return { list: view, filter: 'all' }
  if (view === 'restricted' || view === 'blocked') return { list: 'fans', filter: view }
  return null
}

export type SubscriptionCounts = Partial<Record<FanFilter, number>>

/** `/v1/subscriptions/count`: `subscribers` are the Fans list, `subscriptions` Following. */
export function countsFor(list: FanList | null, payload: Raw | null): SubscriptionCounts {
  const side = list?.id === 'fans' ? 'subscribers' : list?.id === 'following' ? 'subscriptions' : null
  const raw = (side && payload ? payload[side] : null) as Raw | null
  if (!raw) return {}
  const out: SubscriptionCounts = {}
  for (const f of ['all', 'active', 'expired', 'restricted', 'blocked'] as const) {
    if (typeof raw[f] === 'number') out[f] = raw[f] as number
  }
  return out
}
